import { useCallback, useRef, useState } from "react";
import { isAiConsentRefusal } from "../../api/aiConsentRefusal";
import { rawApiFetch } from "../../api/client";
import type { WorkoutLogPill } from "../../api/types";
import { COPY } from "../../copy/firstRun";
import type { CoachArtifact } from "./artifacts/types";
import { readBudgetSpent, type BudgetSpent } from "./budgetSpent";
import { CONNECTION_FAILED_LINE, chatRefusal } from "./chatFailure";
import { createCoachStreamReader } from "./coachStreamReader";

export interface CoachStream {
  isStreaming: boolean;
  /**
   * `turn` is the id the shell gave the assistant message this question will be answered in. The
   * announcements that can come after `done` (`onArtifact`, `onFollowups`) carry it back, because by then
   * the person may have sent the next question and "the last message" is no longer this turn's.
   */
  send: (sessionId: string, text: string, turn: string) => Promise<void>;
  stop: () => void;
}

/**
 * SSE wire handling for the AI coach chat, lifted out of AICoach.tsx.
 *
 * Deliberately ignorant of the message list: it reads `/api/coach/chat`'s
 * event stream (the parsing is `createCoachStreamReader`, whose grammar is
 * `contracts/coach-stream.json`) and announces what it saw via callbacks
 * (`onDelta`, `onFollowups`, `onError`, `onDone`). The shell decides what each
 * callback means for `messages` state — this hook never touches it.
 *
 * Only a turn that reached `done` has a reply to show (S24, #449). A turn that
 * ends any other way shows none of what arrived: an `error` event shows its
 * line, a connection that fails or a body that ends with neither `done` nor
 * `error` shows `CONNECTION_FAILED_LINE`, and the user's own abort (`onAbort`)
 * shows nothing in its place.
 *
 * A stream outlives its `done`: the cards picked for the reply and the
 * follow-ups come after it, and `isStreaming` is already false, so the person
 * can send the next question meanwhile. Two streams then overlap, and the
 * older one's late events are bound to its own turn (#449, P9): they carry its
 * `turn`; a failure of its connection after `done` changes nothing (the reply
 * is final, and a lost card or follow-up is simply absent, as when the server
 * refuses them); and its end does not clear the newer turn's abort handle.
 * The older stream is never aborted by the newer send: the server stores the
 * reply only after the card pass, and a disconnect before it would lose the
 * reply a reload should show.
 *
 * The connection-failure line is English, unconditionally. It is chrome, not
 * coach content, and the coach's chrome is English-only (design decision D9) —
 * which is why this hook no longer takes a `language`.
 */
export function useCoachStream(opts: {
  onDelta: (chunk: string) => void;
  /** The follow-ups of `turn`, which come after its `done`: the shell shows them only while that turn is the latest. */
  onFollowups: (f: (string | WorkoutLogPill)[], turn: string) => void;
  onError: (message: string) => void;
  onDone: () => void;
  /** A final card of `turn`. One announced after `done` belongs to that turn's message, not to the last one. */
  onArtifact: (artifact: CoachArtifact, turn: string) => void;
  /**
   * The user ended the turn themselves before its `done`: the composer's stop control, or "New" pressed while a
   * reply was streaming (`stop()`). Nothing of the reply is final and the server stores none of it, so the shell
   * shows none of what had arrived and takes the turn's pending bubble away. Not an error and not a refusal: no
   * line replaces the reply, and the question stays where it is (the server has it, as for any turn). Called
   * after `isStreaming` went false.
   */
  onAbort: () => void;
  /**
   * The turn was refused because the user's AI budget is spent (a 429 whose
   * `reason` is `ai_budget_spent`, #394). Not an error: the shell shows it as
   * the coach's own message. The coach's rate limit, also a 429, still comes
   * through `onError`.
   */
  onBudgetSpent: (spent: BudgetSpent) => void;
  /**
   * The chat POST was refused as busy before the stream started (#391's 503 with
   * a usable `Retry-After`; owner ruling CAP-O42): nothing was stored and nothing
   * went to the model, so the shell gives the question back to the composer, as
   * it does for a spent budget. Called **after** `onError` has shown the busy
   * line, which is an ordinary failed turn's. A busy marker INSIDE the stream
   * never calls it: the server has stored that question, and sending it again
   * would show it twice.
   */
  onBusyRefused: () => void;
  /**
   * The chat POST was refused because the user has not agreed to AI processing (a 403 whose body carries
   * `reason: "ai_consent_required"`, #398/#399, CS-D19): the consent was withdrawn on another device, say.
   * Like a busy refusal it happened before the stream, so nothing was stored and no model was asked; the
   * turn's own line (`COPY.aiConsentRefused`) is already in the bubble (`onError` ran first), and the
   * shell shuts the composer, gives the question back, and never sends again. Optional so a shell that
   * has no consent state still shows the line and not the raw body.
   */
  onConsentRefused?: () => void;
}): CoachStream {
  const [isStreaming, setIsStreaming] = useState(false);

  const abortRef = useRef<AbortController | null>(null);

  // Always-latest callbacks, read via .current inside `send` so that `send`
  // and `stop` can stay referentially stable (empty dep arrays) without
  // closing over a stale `opts` from whichever render created them. This is
  // a render-phase mutation rather than an effect on purpose: an effect only
  // commits after paint, so a chunk delivered between commit and effect would
  // read a stale callback. The mutation itself is idempotent (StrictMode's
  // double-render just assigns the same value twice), so there's nothing an
  // effect would buy here — don't "fix" this into a `useEffect`.
  const optsRef = useRef(opts);
  optsRef.current = opts;

  const stop = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  // ── Finalize stream ────────────────────────────────────────────────────────
  // Order here is normalized, not preserved: the original called
  // `finalizePendingMessage(…)` *then* `setIsStreaming(false)` at six of its
  // seven call sites, and only the `!reader` branch flipped `isStreaming`
  // first. That base was internally inconsistent; this hook always flips
  // `isStreaming` off before announcing completion. It's safe only because
  // both setters run in the same async continuation and React 19 batches
  // them into a single render, so no intermediate frame can show
  // `isStreaming === false` beside an un-revealed `pending` bubble — do not
  // wrap either call in `flushSync`, which would split that batch and expose
  // the reordering.
  //
  // Content-wise this hook holds no reveal buffer of its own — the shell has
  // been accumulating chunks itself via `onDelta` as they streamed in, so
  // `onDone` takes no argument. Passing a string routes to `onError` (even an
  // empty string — this must stay a `!== undefined` check, not a truthiness
  // check, to match the original's `overrideContent ?? buffer` semantics for
  // an empty error body); omitting it routes to `onDone`.
  const finalize = useCallback((errorMessage?: string) => {
    setIsStreaming(false);
    if (errorMessage !== undefined) {
      optsRef.current.onError(errorMessage);
    } else {
      optsRef.current.onDone();
    }
  }, []);

  const send = useCallback(
    async (sessionId: string, text: string, turn: string) => {
      setIsStreaming(true);

      const controller = new AbortController();
      abortRef.current = controller;
      // This turn's `done` was announced: its reply is final, and what the stream still holds is extras.
      let answered = false;

      try {
        const res = await rawApiFetch("/api/coach/chat", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            session_id: sessionId,
            message: { role: "user", content: text },
          }),
          signal: controller.signal,
        });

        if (!res.ok) {
          const errText = await res.text().catch(() => "Connection error");
          const spent = readBudgetSpent(res.status, errText, res.headers.get("Retry-After"));
          if (spent !== null) {
            // Before `finalize`, which would announce an error: this is the
            // coach telling the user their allowance is used up.
            setIsStreaming(false);
            optsRef.current.onBudgetSpent(spent);
            return;
          }
          // The consent's refusal, by its marker (the status alone is also an account pending deletion).
          // The turn's own line, never the raw body, and never sent again from this page.
          if (isAiConsentRefusal(res.status, errText)) {
            finalize(COPY.aiConsentRefused);
            optsRef.current.onConsentRefused?.();
            return;
          }
          // The words: a busy 503 reads as the deck's busy line and every other
          // refusal as its `detail` sentence (`chatFailure.ts`). A busy refusal is
          // never sent again on its own (a chat turn is a write, CAP-O20).
          const refusal = chatRefusal(res.status, res.headers.get("Retry-After"), errText);
          finalize(refusal.line);
          // And, for a busy one only, the question: it never reached the model and
          // the server stored nothing, so it goes back to the composer (CAP-O42).
          if (refusal.kind === "busy") optsRef.current.onBusyRefused();
          return;
        }

        const reader = res.body?.getReader();
        if (!reader) {
          // A 200 with no body has no `done` and no `error`: nothing final, as for a body cut short.
          finalize(CONNECTION_FAILED_LINE);
          return;
        }

        // The parsing itself is `createCoachStreamReader` (#448), a pure
        // function of the text, which the recorded transcripts replay. This
        // loop only reads bytes, decodes them, and stops where it says so.
        const decoder = new TextDecoder();
        const stream = createCoachStreamReader({
          onDelta: (chunk) => optsRef.current.onDelta(chunk),
          onFollowups: (f) => optsRef.current.onFollowups(f, turn),
          onArtifact: (artifact) => optsRef.current.onArtifact(artifact, turn),
          onError: (line) => finalize(line),
          onDone: () => {
            answered = true;
            finalize();
          },
        });

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          if (!stream.push(decoder.decode(value, { stream: true }))) return;
        }

        stream.end();
      } catch (err: unknown) {
        if (answered) {
          // The connection failed after `done`. The reply is final and stays; the cards and follow-ups that
          // were still to come are simply absent, as when the server refuses them. Nothing is announced:
          // this stream no longer owns `isStreaming` or a pending bubble (the next turn may), and a line
          // here would land on that turn.
          return;
        }
        if (err instanceof DOMException && err.name === "AbortError") {
          // The user's own `stop()`. What arrived before it never reached `done`, so none of it is revealed:
          // the server stores nothing of it either, and a reload would show the question alone.
          setIsStreaming(false);
          optsRef.current.onAbort();
        } else {
          finalize(CONNECTION_FAILED_LINE);
        }
      } finally {
        // Only this turn's own handle: after `done` a newer turn may have taken the slot, and Stop must
        // still reach it.
        if (abortRef.current === controller) abortRef.current = null;
      }
    },
    [finalize]
  );

  return { isStreaming, send, stop };
}
